<script>
import { onMounted, ref, computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useModalStore } from "@/stores/modal.js";
import { useGarageStore } from "@/stores/garage.js";

export default {
  setup() {
    // I18n
    const { t, tm } = useI18n();
    const translationPrefix = ref('modal.add_vehicle');

    // Stores
    const modalStore = useModalStore();
    const garageStore = useGarageStore();

    // Variables
    const isMakesLoading  = ref(true);
    const isModelsLoading  = ref(false);
    const isTrimsLoading  = ref(false);

    onMounted(async () => {
      garageStore.yearRange = await garageStore.getYearRange();
      await garageStore.getMakes()
        .then(() => {
          setTimeout(() => {
            isMakesLoading.value = false;
          }, 250);
        });
    });

    // Dropdown selections disabled if previous selection not made
    const makeDropdownDisabled = computed(() => {
      return !(modalStore.newVehicle.year && modalStore.newVehicle.year !== t('modal.add_vehicle.year_placeholder'));
    });

    const modelDropdownDisabled = computed(() => {
      return !(modalStore.newVehicle.make_id && modalStore.newVehicle.make_id !== t('modal.add_vehicle.make_placeholder'));
    });

    const trimDropdownDisabled = computed(() => {
      return !(modalStore.newVehicle.vehicle_model_id && modalStore.newVehicle.vehicle_model_id !== t('modal.add_vehicle.model_placeholder'));
    });

    // Mileage validation
    const mileageError = computed(() => {
      const mileage = modalStore.newVehicle.mileage;
      if (mileage === null || mileage === undefined || mileage === '') return null;
      if (mileage < 0) return 'Mileage cannot be negative';
      if (mileage > 10000000) return 'Mileage cannot exceed 10,000,000 miles';
      return null;
    });

    const getModels = async () => {
      isModelsLoading.value = true;

      await garageStore.getModels(modalStore.newVehicle.year, modalStore.newVehicle.make_id)
        .then(() => {
          setTimeout(() => {
            isModelsLoading.value = false;
          }, 250);
        });
    }

    // Reset trim/model
    watch(() => modalStore.newVehicle.year, () => {
      if (modalStore.newVehicle.vehicle_edition_id) {
        modalStore.newVehicle.vehicle_edition_id = t('modal.add_vehicle.trim_placeholder');
      }
      if (modalStore.newVehicle.vehicle_model_id) {
        modalStore.newVehicle.vehicle_model_id = t('modal.add_vehicle.model_placeholder');
      }

      // Make isn't dependent on year, this will re-trigger call for models which is year dependent
      if (modalStore.newVehicle.make_id) {
        let tempVal = modalStore.newVehicle.make_id;
        modalStore.newVehicle.make_id = null;
        modalStore.newVehicle.make_id = tempVal;

        new Promise((resolve) => {
          modalStore.newVehicle.make_id = null;
          resolve();
        })
          .then(() => {
            return new Promise((resolve) => {
              modalStore.newVehicle.make_id = tempVal;
              resolve();
            });
          })
      }
    });

    watch(() => modalStore.newVehicle.make_id, async () => {
      // Reset trim/model
      if (modalStore.newVehicle.vehicle_edition_id) {
        modalStore.newVehicle.vehicle_edition_id = t('modal.add_vehicle.trim_placeholder');
      }
      if (modalStore.newVehicle.vehicle_model_id) {
        modalStore.newVehicle.vehicle_model_id = t('modal.add_vehicle.model_placeholder');
      }

      if (modalStore.newVehicle.year && modalStore.newVehicle.year !== t('modal.add_vehicle.year_placeholder') && modalStore.newVehicle.make_id && modalStore.newVehicle.make_id !== t('modal.add_vehicle.make_placeholder')) {
        await getModels();
      }
    });

    watch(() => modalStore.newVehicle.vehicle_model_id, async () => {
      // Reset trim
      if (modalStore.newVehicle.vehicle_edition_id) {
        modalStore.newVehicle.vehicle_edition_id = t('modal.add_vehicle.trim_placeholder');
      }

      if (modalStore.newVehicle.year && modalStore.newVehicle.make_id && modalStore.newVehicle.vehicle_model_id) {
        isTrimsLoading.value = true;
        await garageStore.getTrims(modalStore.newVehicle.year, modalStore.newVehicle.make_id, modalStore.newVehicle.vehicle_model_id)
          .then(() => {
            setTimeout(() => {
              isTrimsLoading.value = false;
            }, 250);
          });
      }
    });

    return {
      t,
      tm,
      translationPrefix,
      modalStore,
      garageStore,
      isMakesLoading,
      isModelsLoading,
      isTrimsLoading,
      makeDropdownDisabled,
      modelDropdownDisabled,
      trimDropdownDisabled,
      mileageError,
    }
  },
};
</script>

<template>
  <div class="justify-center items-center w-full overflow-auto">
    <div class="px-8 py-4">
      <div class="flex flex-col md:flex-row justify-between items-center pb-5 border-b border-gray-300 space-x-0 md:space-x-2">
        <div class="w-full md:w-1/4 mb-2 md:mb-0">
          <label for="year_dropdown" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.year_label`) }}</label>
          <div class="relative">
            <select
              id="year_dropdown"
              v-model="modalStore.newVehicle.year"
              :class="`appearance-none w-full mt-1 p-2 px-4 rounded-md ${modalStore.validationErrors.year ? 'outline outline-red-400 focus:ring focus:ring-red-400' : 'border border-gray-300 focus:outline-none focus:ring focus:ring-purple-300'}`"
            >
              <option :value="t(`${translationPrefix}.year_placeholder`)" disabled>{{ t(`${translationPrefix}.year_placeholder`) }}</option>
              <option v-for="year in garageStore.yearRange" :key="year" :value="year">{{ year }}</option>
            </select>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <img src="../../../assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" class="mt-1">
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/4  mb-2 md:mb-0">
          <label for="make_dropdown" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.make_label`) }}</label>
          <div class="relative">
            <select
              v-if="!isMakesLoading"
              id="make_dropdown"
              v-model="modalStore.newVehicle.make_id"
              :disabled="makeDropdownDisabled"
              :class="`appearance-none w-full mt-1 p-2 px-4 rounded-md ${modalStore.validationErrors.make_id ? 'outline outline-red-400 focus:ring focus:ring-red-400' : 'border border-gray-300 focus:outline-none focus:ring focus:ring-purple-300'}`"
            >
              <option :value="t(`${translationPrefix}.make_placeholder`)" disabled>{{ t(`${translationPrefix}.make_placeholder`) }}</option>
              <option v-for="make in garageStore.makes" :key="make.id" :value="make.id">{{ make.name }}</option>
            </select>
            <div v-else class="block appearance-none w-full mt-1 p-2 px-4 rounded-md h-[40px] border border-gray-300 py-2 px-4 pr-8 leading-tight loading-input cursor-not-allowed">
              <div class="flex justify-center items-center">
                <svg class="animate-spin -ml-1 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="ml-2 text-gray-500">{{ `${t('my_garage.loading')}...` }}</span>
              </div>
            </div>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <img src="../../../assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" class="mt-1">
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/4 mb-2 md:mb-0">
          <label for="model_dropdown" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.model_label`) }}</label>
          <div class="relative">
            <select
              v-if="!isModelsLoading"
              id="model_dropdown"
              v-model="modalStore.newVehicle.vehicle_model_id"
              :disabled="modelDropdownDisabled"
              :class="`appearance-none w-full mt-1 p-2 px-4 rounded-md ${modalStore.validationErrors.vehicle_model_id ? 'outline outline-red-400 focus:ring focus:ring-red-400' : 'border border-gray-300 focus:outline-none focus:ring focus:ring-purple-300'}`"
            >
              <option :value="t(`${translationPrefix}.model_placeholder`)" disabled>{{ t(`${translationPrefix}.model_placeholder`) }}</option>
              <option v-for="model in garageStore.models" :key="model.id" :value="model.vehicle_model_id">{{ model.vehicle_name }}</option>
            </select>
            <div v-else class="block appearance-none w-full mt-1 p-2 px-4 rounded-md h-[40px] border border-gray-300 py-2 px-4 pr-8 leading-tight loading-input cursor-not-allowed">
              <div class="flex justify-center items-center">
                <svg class="animate-spin -ml-1 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="ml-2 text-gray-500">{{ `${t('my_garage.loading')}...` }}</span>
              </div>
            </div>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <img src="../../../assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" class="mt-1">
            </div>
          </div>
        </div>
        <div class="w-full md:w-1/4 mb-2 md:mb-0">
          <label for="trim_dropdown" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.trim_label`) }}</label>
          <div class="relative">
            <select
              v-if="!isTrimsLoading"
              id="trim_dropdown"
              v-model="modalStore.newVehicle.vehicle_edition_id"
              :disabled="trimDropdownDisabled"
              :class="`appearance-none w-full mt-1 p-2 px-4 rounded-md ${modalStore.validationErrors.vehicle_edition_id ? 'outline outline-red-400 focus:ring focus:ring-red-400' : 'border border-gray-300 focus:outline-none focus:ring focus:ring-purple-300'}`"
            >
              <option :value="t(`${translationPrefix}.trim_placeholder`)" disabled>{{ t(`${translationPrefix}.trim_placeholder`) }}</option>
              <option v-for="trim in garageStore.trims" :key="trim" :value="trim.vehicle_edition_id">{{ trim.vehicle_edition_name }}</option>
            </select>
            <div v-else class="block appearance-none w-full mt-1 p-2 px-4 rounded-md h-[40px] border border-gray-300 py-2 px-4 pr-8 leading-tight loading-input cursor-not-allowed">
              <div class="flex justify-center items-center">
                <svg class="animate-spin -ml-1 h-5 w-5 text-gray-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle class="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" stroke-width="4"></circle>
                  <path class="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                <span class="ml-2 text-gray-500">{{ `${t('my_garage.loading')}...` }}</span>
              </div>
            </div>
            <div class="pointer-events-none absolute inset-y-0 right-0 flex items-center px-2 text-gray-700">
              <img src="../../../assets/icons/keyboard_arrow_down_24dp_FILL0_wght400_GRAD0_opsz24.svg" class="mt-1">
            </div>
          </div>
        </div>
      </div>
      <div class="pt-4 mb-4">
        <!-- <p class="font-semibold text-lg text-gray-600">{{ t(`${translationPrefix}.benefits_mileage`) }}</p> -->
        <!-- <p class="text-md text-gray-600">{{ t(`${translationPrefix}.benefits_mileage_text`) }}</p> -->
      </div>
      <div class="mb-8">
        <label for="mileage" class="block text-gray-600 font-semibold">{{ t(`${translationPrefix}.mileage_label`) }}</label>
        <input 
          type="number" 
          id="mileage" 
          name="mileage" 
          v-model="modalStore.newVehicle.mileage" 
          min="0" 
          max="10000000"
          :class="`w-full md:w-1/4 mt-1 p-2 px-4 rounded-md focus:outline-none ${mileageError ? 'border-red-400 focus:ring focus:ring-red-400' : 'border border-gray-300 focus:ring focus:ring-purple-300'}`"
          :placeholder="t(`${translationPrefix}.mileage_placeholder`)"
        >
        <div v-if="mileageError" class="text-red-500 text-sm mt-1">{{ mileageError }}</div>
      </div>

      <hr/>
      <div class="pt-4 mb-4">
        <!-- <p class="text-md text-gray-600">{{ t(`modal.add_vehicle.missing_option_disclaimer`) }}</p> -->
        <p v-for="blurb in tm('modal.add_vehicle.missing_option_disclaimer')" class="text-gray-600" v-html="blurb"></p>

      </div>

    </div>
  </div>
</template>

<style lang="scss" scoped>
@import '@/assets/styles/components/modals/garage';
</style>
